import { PageSubtitle } from "@/shared/ui/page-subtitle"
import { PageHeader } from "@/widgets/page-header"
import { useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { toLocaleDateString } from "@/shared/lib/date-formatters"
import { PrizesTable } from "./prizes-table"
import { DrawStatistics } from "./draw-statistics"
import { DefaultStatistics } from "./default-statistics"
import { 
    fetchChannels, 
    fetchMarketingCampaign, 
    fetchMarketingCampaignStatistics, 
    fetchParticipations, 
    fetchParticipationsCount, 
    fetchPrizes, 
    fetchRegistrationsCount, 
    selectChannelsFirstLoad, 
    selectMarketingCampaign, 
    selectParticipationsFirstLoad, 
    selectParticipationsPage, 
    selectPrizesFirstLoad, 
    selectPrizesPage 
} from "@/entities/marketing-campaign"
import { ParticipationsTable } from "./participations-table"

export const MarketingCampaignPage = () => {
    const isFirstRender = useRef(true)
    const { id } = useParams()
    const campaign = useAppSelector(selectMarketingCampaign)
    const prizesFirstLoad = useAppSelector(selectPrizesFirstLoad)
    const prizesPage = useAppSelector(selectPrizesPage)
    const participationsFirstLoad = useAppSelector(selectParticipationsFirstLoad)
    const participationsPage = useAppSelector(selectParticipationsPage)
    const channelsFirstLoad = useAppSelector(selectChannelsFirstLoad)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    
    useEffect(() => {
        if (id !== campaign.id) {
            dispatch(fetchMarketingCampaign(id))
            .unwrap()
            .catch(() => {
                navigate('/404', { replace: true })
            })
        }
    }, [id])

    useEffect(() => {
        if (!isFirstRender.current) {
            dispatch(fetchPrizes())
            dispatch(fetchParticipationsCount())
            dispatch(fetchMarketingCampaignStatistics())
            dispatch(fetchRegistrationsCount())
        }
    }, [prizesFirstLoad, prizesPage, campaign])

    useEffect(() => {
        if (!isFirstRender.current) {
            dispatch(fetchParticipations())
        }
    }, [participationsFirstLoad, participationsPage, campaign])

    useEffect(() => {
        if (channelsFirstLoad) {
            dispatch(fetchChannels())
        }
        isFirstRender.current = false
    }, [])

    return (
        <div className="_page_container">
            <PageHeader title="Маркетинговые кампании" />
            <div className="flexbox">
                <PageSubtitle title="Статистика " />
                <div className="flex gap-2 items-center">
                    <div className="px-2 py-1 bg-color-primary text-white rounded-md font-medium text-sm">
                        {campaign.name}
                    </div>
                    <div className="px-2 py-1 bg-sky-700 text-white rounded-md font-medium text-sm">
                        {campaign.is_draw ? "Розыгрыш" : "Стандартная кампания"}
                    </div>
                    <div className="px-2 py-1 bg-teal-600 text-white rounded-md font-medium text-sm">
                        {toLocaleDateString(campaign.start_date)} - {toLocaleDateString(campaign.end_date)}
                    </div>
                    {campaign.is_finished
                        ? <div className="px-2 py-1 bg-slate-600 text-white rounded-md font-medium text-sm">
                            Завершена
                        </div>
                        : <div className="px-2 py-1 bg-yellow-500 text-white rounded-md font-medium text-sm">
                            Ожидает завершения
                        </div>
                    }
                    {campaign.is_active
                        ? <div className="px-2 py-1 bg-emerald-600 text-white rounded-md font-medium text-sm">
                            Активна
                        </div>
                        : <div className="px-2 py-1 bg-color-danger text-white rounded-md font-medium text-sm">
                            Приостановлена
                        </div>
                    }
                </div>
                {campaign.is_draw
                    ? <DrawStatistics />
                    : <DefaultStatistics />
                }
                <PrizesTable />
                <ParticipationsTable />
            </div>
        </div>
    )
}